import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Send,
    Bot,
    User,
    Loader2,
    Sparkles,
    MessageCircle,
    ArrowLeft,
    ArrowRight,
    FileText
} from 'lucide-react';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatPage = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const dateLocale = isRTL ? ar : enUS;
    const BackIcon = isRTL ? ArrowRight : ArrowLeft;
    const [searchParams] = useSearchParams();
    const reportIdParam = searchParams.get('report');
    const questionParam = searchParams.get('q');
    const { user } = useAuth();
    const [reports, setReports] = useState([]);
    const [selectedReport, setSelectedReport] = useState(reportIdParam || '');
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');

    useEffect(() => {
        if (questionParam) setInput(questionParam);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questionParam]);
    const [loading, setLoading] = useState(false);
    const [loadingReports, setLoadingReports] = useState(true);
    const scrollRef = useRef(null);

    const suggestedQuestions = isRTL ? [
        "ماذا يعني مستوى الهيموجلوبين؟",
        "لماذا قد يكون الكولسترول مرتفعاً؟",
        "ما الأطعمة التي تساعد على تحسين نتائجي؟",
        "هل يجب أن أقلق بشأن أي قيم؟",
        "كيف يمكنني تحسين مستوى فيتامين د؟"
    ] : [
        "What does my hemoglobin level mean?",
        "Why might my cholesterol be high?",
        "What foods can help improve my results?",
        "Should I be concerned about any values?",
        "How can I improve my vitamin D levels?"
    ];

    useEffect(() => {
        const fetchReports = async () => {
            try {
                const response = await axios.get(`${API}/reports`);
                setReports(response.data);
            } catch (error) {
                console.error('Failed to fetch reports:', error);
            } finally {
                setLoadingReports(false);
            }
        };

        if (user) fetchReports();
    }, [user]);

    useEffect(() => {
        // Add welcome message
        setMessages([{
            role: 'assistant',
            content: "Hello! I'm your AI health assistant. I can help you understand your lab results and answer questions about your health data. Select a report to get started, or ask me a general health question.",
            timestamp: new Date().toISOString()
        }]);
    }, []);

    useEffect(() => {
        // Scroll to bottom on new messages
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    const handleSend = async (messageText = input) => {
        if (!messageText.trim()) return;

        const userMessage = {
            role: 'user',
            content: messageText,
            timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setLoading(true);

        try {
            const response = await axios.post(`${API}/chat`, {
                message: messageText,
                report_id: selectedReport === 'none' ? null : (selectedReport || null),
                language: i18n.language
            });

            const assistantMessage = {
                role: 'assistant',
                content: response.data.response,
                timestamp: response.data.timestamp
            };

            setMessages(prev => [...prev, assistantMessage]);
        } catch (error) {
            console.error('Chat error:', error);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: isRTL ? "عذراً، لم أتمكن من معالجة سؤالك الآن. يرجى المحاولة مرة أخرى." : "I'm sorry, I couldn't process your question right now. Please try again.",
                timestamp: new Date().toISOString()
            }]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="chat-page">
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
                <Link to="/dashboard">
                    <Button variant="ghost" size="icon" className="rounded-full" data-testid="back-btn">
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                </Link>
                <div className="flex-1">
                    <h1 className="text-2xl font-bold text-stone-800">AI Health Assistant</h1>
                    <p className="text-stone-500">Ask questions about your lab results</p>
                </div>
            </div>

            {/* Report Selector */}
            <Card className="border-0 shadow-lg shadow-stone-200/50 mb-6" data-testid="report-selector">
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        <div className="flex items-center gap-2 text-stone-600">
                            <FileText className="w-5 h-5" />
                            <span className="font-medium">Context Report:</span>
                        </div>
                        <Select value={selectedReport} onValueChange={setSelectedReport}>
                            <SelectTrigger className="w-full sm:w-80 rounded-xl" data-testid="report-select">
                                <SelectValue placeholder="Select a report (optional)" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">No specific report</SelectItem>
                                {reports.map((report) => (
                                    <SelectItem key={report.id} value={report.id}>
                                        {report.panel_type || 'Lab Report'} - {format(new Date(report.upload_date), 'MMM d, yyyy')}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {selectedReport && (
                            <Link to={`/report/${selectedReport}`}>
                                <Button variant="outline" size="sm" className="rounded-full">
                                    View Report
                                </Button>
                            </Link>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Chat Container */}
            <Card className="border-0 shadow-xl shadow-stone-200/50" data-testid="chat-container">
                <CardContent className="p-0">
                    {/* Messages */}
                    <ScrollArea className="h-[400px] sm:h-[450px] p-6" ref={scrollRef}>
                        <div className="space-y-6">
                            {messages.map((message, index) => (
                                <div
                                    key={index}
                                    className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
                                    data-testid={`message-${index}`}
                                >
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                        message.role === 'user' 
                                            ? 'bg-teal-500' 
                                            : 'bg-gradient-to-br from-teal-400 to-teal-600'
                                    }`}>
                                        {message.role === 'user' ? (
                                            <User className="w-5 h-5 text-white" />
                                        ) : (
                                            <Bot className="w-5 h-5 text-white" />
                                        )}
                                    </div>
                                    <div className={`max-w-[80%] ${message.role === 'user' ? 'text-right' : ''}`}>
                                        <div className={`inline-block p-4 rounded-2xl ${
                                            message.role === 'user'
                                                ? 'bg-teal-500 text-white rounded-tr-none'
                                                : 'bg-stone-100 text-stone-800 rounded-tl-none'
                                        }`}>
                                            <p className="whitespace-pre-wrap">{message.content}</p>
                                        </div>
                                        <p className="text-xs text-stone-400 mt-1 px-1">
                                            {format(new Date(message.timestamp), 'h:mm a')}
                                        </p>
                                    </div>
                                </div>
                            ))}
                            {loading && (
                                <div className="flex gap-3" data-testid="loading-message">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
                                        <Bot className="w-5 h-5 text-white" />
                                    </div>
                                    <div className="bg-stone-100 rounded-2xl rounded-tl-none p-4">
                                        <div className="flex items-center gap-2 text-stone-600">
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            <span>Thinking...</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </ScrollArea>

                    {/* Suggested Questions */}
                    {messages.length <= 1 && (
                        <div className="px-6 pb-4" data-testid="suggested-questions">
                            <p className="text-sm text-stone-500 mb-3 flex items-center gap-2">
                                <Sparkles className="w-4 h-4" />
                                Try asking:
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {suggestedQuestions.map((question, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleSend(question)}
                                        className="px-3 py-2 bg-stone-100 hover:bg-teal-50 text-stone-700 hover:text-teal-700 rounded-full text-sm transition-colors"
                                        data-testid={`suggested-${index}`}
                                    >
                                        {question}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Input */}
                    <div className="p-4 border-t border-stone-100">
                        <div className="flex gap-3">
                            <Input
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Ask about your lab results..."
                                className="flex-1 h-12 rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500/20"
                                disabled={loading}
                                data-testid="chat-input"
                            />
                            <Button
                                onClick={() => handleSend()}
                                disabled={loading || !input.trim()}
                                className="h-12 px-6 bg-teal-500 hover:bg-teal-600 text-white rounded-xl"
                                data-testid="send-btn"
                            >
                                {loading ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    <Send className="w-5 h-5" />
                                )}
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Disclaimer */}
            <div className="mt-6 p-4 bg-stone-100 rounded-2xl">
                <p className="text-sm text-stone-600 text-center">
                    ⚠️ The AI assistant provides educational information only. It cannot diagnose conditions or replace professional medical advice. 
                    Always consult your healthcare provider for medical decisions.
                </p>
            </div>
        </div>
    );
};

export default ChatPage;
